import { ElementType, ReactNode, useRef } from "react"

import { UseSelectStateChange, useSelect } from "downshift"
import { css, cx } from "@emotion/css"

import DropdownIcon from "ui/icons/chevron-down"
import Option from "ui/inputs/Option"
import Popover from "@material-ui/core/Popover"
import { styles as ghost_styles } from "./GhostTextInput"
import { useI18n } from "providers/I18n"

const styles = {
  container: css`
    position: relative;
    box-sizing: border-box;
    display: inline-block;
  `,
  select: css`
    background-color: transparent;
    min-width: 64px;
    line-height: 20px;
    user-select: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-right: 32px;

    --icon-opacity: 0;

    .__icon {
      opacity: var(--icon-opacity);
      transition: opacity 250ms cubic-bezier(0.4, 0, 0.2, 1);
      color: gray;
    }

    :enabled:hover {
      cursor: pointer;
      --icon-opacity: 1;
    }

    :focus,
    :focus-within {
      --icon-opacity: 1;
    }
  `,

  icon: css`
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;

    display: inline-flex;
    align-items: center;
    justify-content: center;

    padding-right: 8px;
    padding-left: 8px;
  `,

  //
  list: css`
    margin: 0;
    padding: 8px;
    position: relative;
    list-style: none;
    outline: none;
    overflow-y: auto;
    min-width: 120px;
  `,

  text: css`
    min-height: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  placeholder: css`
    color: var(--grey, grey);
  `,
}

export interface IOption {
  value: string
  label: string
  disabled?: boolean
}

export type SelectedOptionChanges<Opt extends IOption = IOption> = {
  value?: string
  option?: Opt | null
}

type GhostSelectOwnProps<Opt extends IOption = IOption> = {
  IconComponent?: ElementType
  id?: string
  placeholder?: string
  disabled?: boolean
  error?: boolean
  //
  options: Opt[]
  getOptionDisabled?: (option: Opt) => boolean
  value: Opt["value"]
  onSelectedOptionChange: (changes: SelectedOptionChanges<Opt>) => void
  renderOption?: ((option: Opt) => ReactNode) | undefined
  className?: string
  menuClassName?: string
  name?: string
}

function GhostSelect<Opt extends IOption = IOption>(
  props: GhostSelectOwnProps<Opt>,
) {
  const { t } = useI18n()
  const {
    IconComponent = DropdownIcon,
    id,
    placeholder = "select.option.placeholder",
    disabled = false,
    error = false,
    options,
    getOptionDisabled,
    //
    value,
    onSelectedOptionChange,
    renderOption,
    className,
    menuClassName,
    name,
  } = props

  const _id = id || "ghost_select_id"
  const selectedItem = options.find((it) => it.value === value)
  const onSelectedItemChange = (changes: UseSelectStateChange<Opt>) => {
    onSelectedOptionChange({
      value: changes.selectedItem?.value,
      option: changes.selectedItem,
    })
  }

  const anchorEl = useRef<HTMLButtonElement>(null)

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    closeMenu,
  } = useSelect({ items: options, onSelectedItemChange })

  const wrapperCn = cx(
    "ghost-select-wrapper",
    ghost_styles.root,
    styles.select,
    className,
    { [ghost_styles.fullWidth]: false },
  )
  const iconWrapperCn = cx("ghost-select-icon", "__icon", styles.icon)

  return (
    <div className={styles.container}>
      <button
        name={name}
        className={wrapperCn}
        aria-describedby={_id}
        data-disabled={disabled}
        data-error={error}
        type="button"
        disabled={disabled}
        {...getToggleButtonProps({
          ref: anchorEl,
        })}
      >
        {selectedItem ? (
          <span className={styles.text}>{selectedItem?.label}</span>
        ) : (
          <span className={cx(styles.text, styles.placeholder)}>
            {t(placeholder)}
          </span>
        )}
        <span className={iconWrapperCn}>
          <IconComponent />
        </span>
      </button>
      <Popover
        disablePortal
        open={isOpen}
        onClose={closeMenu}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <ul
          {...getMenuProps({}, { suppressRefError: true })}
          className={cx(styles.list, menuClassName)}
        >
          {isOpen
            ? options.map((option, index) => {
                const disabled = getOptionDisabled
                  ? getOptionDisabled(option)
                  : false
                const highlighted = index === highlightedIndex
                return (
                  <Option
                    key={`${option.value}-${index}`}
                    disabled={disabled}
                    highlighted={highlighted}
                    {...getItemProps({
                      item: option,
                      index,
                      disabled,
                    })}
                  >
                    {!!renderOption ? (
                      renderOption(option)
                    ) : (
                      <span>{option.label}</span>
                    )}
                  </Option>
                )
              })
            : null}
        </ul>
      </Popover>
    </div>
  )
}

export default GhostSelect
